import * as React from "react";
import {
  Legend as RechartsLegend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import { cn } from "@/lib/utils";

const THEMES = { light: "", dark: ".dark" };

const ChartContext = React.createContext(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

function ChartContainer({ id, className, children, config, ...props }) {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        className={cn(
          "flex aspect-video justify-center text-xs text-gray-300 [&_.recharts-cartesian-axis-tick_text]:fill-gray-500 [&_.recharts-cartesian-grid_line]:stroke-white/10 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-white/20 [&_.recharts-layer]:outline-none [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-white/5 [&_.recharts-surface]:outline-none",
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

function ChartStyle({ id, config }) {
  const colorConfig = Object.entries(config).filter(([, item]) => item.theme || item.color);

  if (!colorConfig.length) return null;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color = itemConfig.theme?.[theme] ?? itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .filter(Boolean)
  .join("\n")}
}
`)
          .join("\n"),
      }}
    />
  );
}

const ChartTooltip = RechartsTooltip;

function ChartTooltipContent({
  active,
  payload,
  className,
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  formatter,
  nameKey,
  labelKey,
}) {
  const { config } = useChart();

  if (!active || !payload?.length) return null;

  const firstItem = payload[0];
  const labelConfigKey = `${labelKey ?? firstItem?.dataKey ?? firstItem?.name ?? "value"}`;
  const labelConfig = getPayloadConfigFromPayload(config, firstItem, labelConfigKey);
  const displayLabel = labelFormatter
    ? labelFormatter(label, payload)
    : labelConfig?.label || label;

  return (
    <div className={cn("grid min-w-[9rem] gap-2 rounded-xl border border-white/10 bg-[#08111D]/95 px-3 py-2 text-xs text-white shadow-2xl backdrop-blur", className)}>
      {!hideLabel && displayLabel && (
        <div className="font-bold text-gray-200">{displayLabel}</div>
      )}
      <div className="grid gap-1.5">
        {payload.filter((item) => item.type !== "none").map((item, index) => {
          const key = `${nameKey ?? item.name ?? item.dataKey ?? "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = item.color || item.payload?.fill || itemConfig?.color || "#D4AF37";

          return (
            <div key={`${key}-${index}`} className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2">
                {!hideIndicator && (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: indicatorColor }} />
                )}
                <span className="truncate text-gray-400">{itemConfig?.label || item.name || item.dataKey}</span>
              </div>
              <span className="font-mono font-black tabular-nums text-gray-100">
                {formatter ? formatter(item.value, item.name, item, index, item.payload) : formatTooltipValue(item.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ChartLegend = RechartsLegend;

function ChartLegendContent({ className, hideIcon = false, payload, nameKey }) {
  const { config } = useChart();

  if (!payload?.length) return null;

  return (
    <div className={cn("flex flex-wrap items-center justify-center gap-4 pt-3 text-xs text-gray-400", className)}>
      {payload.filter((item) => item.type !== "none").map((item, index) => {
        const key = `${nameKey ?? item.dataKey ?? "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return (
          <div key={`${key}-${index}`} className="flex items-center gap-1.5">
            {!hideIcon && <span className="h-2 w-2 shrink-0 rounded-[3px]" style={{ backgroundColor: item.color }} />}
            <span>{itemConfig?.label || item.value}</span>
          </div>
        );
      })}
    </div>
  );
}

function getPayloadConfigFromPayload(config, payload, key) {
  if (typeof payload !== "object" || payload === null) return undefined;

  const payloadPayload = typeof payload.payload === "object" && payload.payload !== null
    ? payload.payload
    : undefined;

  let configLabelKey = key;
  if (typeof payload[key] === "string") {
    configLabelKey = payload[key];
  } else if (payloadPayload && typeof payloadPayload[key] === "string") {
    configLabelKey = payloadPayload[key];
  }

  return config[configLabelKey] || config[key];
}

function formatTooltipValue(value) {
  if (typeof value === "number") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value ?? "-");
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
};
